"use client";

import { SellCarRequestResponseDTO } from "@alba-cars/common-modules";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import SuccessStep from "@/app/[locale]/sell-used-car-dubai/components/SuccessStep";
import MultistepForm, { StepData } from "@/components/common/forms/MultiStepForm";
import { createBankValuationRequest } from "@/lib/api/bank-valuation/create-bank-valuation-request";
import { notifyWebhookAboutBankValuationUpdate } from "@/lib/api/bank-valuation/notify-update-webhook";
import { updateBankValuationInspection } from "@/lib/api/bank-valuation/update-bank-valuation-inspection";
import { updateBankValuationInspectionPerfect } from "@/lib/api/bank-valuation/update-bank-valuation-inspection-perfect";
import { getOrCreateLead } from "@/lib/api/lead/get-or-create-lead";
import {
  BankValuationCompleteFormSchemaType,
  BankValuationFirstStepSchemaType,
  BankValuationFormSchemaType,
  ThirdStepSchema as BankValuationImagesStepSchema,
  BankValuationStripePaymentStepSchema,
  createBankDetailsSchema,
  createBankValuationCompleteFormSchema,
  createBankValuationEstimatedPriceStepSchema,
  createBankValuationFirstStepSchema,
  createBankValuationFormSchema,
  createBankValuationSecondStepSchema,
} from "@/schemas/validation/bankValuationSchema";
import { CONTAINER_IDS, LOCAL_STORAGE_KEYS } from "@/utils/constants";
import { DEFAULT_LAT, DEFAULT_LNG } from "./_inspection/inspectionConstants";
import BankValuationBankerDetailsStep from "./BankValuationBankerDetailsStep";
import BankValuationCarDetailsStep from "./BankValuationCarDetailsStep";
import BankValuationEstimatedPriceStep from "./BankValuationEstimatedPriceStep";
import BankValuationImagesStep from "./BankValuationImagesStep";
import BankValuationInspectionStep from "./BankValuationInspectionStep";
import BankValuationPersonalInfoStep from "./BankValuationPersonalInfoStep";
import BankValuationSimpleLocationStep from "./BankValuationSimpleLocationStep";
import BankValuationStripePaymentStep from "./BankValuationStripePaymentStep";

export interface FormStepLabels {
  personalDetails: string;
  carDetails: string;
  estimatedPrice: string;
  bankDetails: string;
  imagesAndDocuments: string;
  inspection: string;
  yourLocation: string; // label for the simple location step
  payment: string;
}

export interface FormStepTitles {
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  step5: string;
  step6: string;
}

export interface ButtonLabels {
  back: string;
  next: string;
  submit: string;
  uploading: string;
}

interface ClientFormWrapperProps {
  title: string;
  description: string;
  initialValues?: BankValuationCompleteFormSchemaType;
  initialStep?: number;
  resumeRequestId?: string;
  stepLabels: FormStepLabels;
  stepTitles: FormStepTitles;
  buttonLabels?: ButtonLabels;
}

const isEnbdBank = (bankName: string | undefined): boolean => {
  if (!bankName) return false;
  return bankName.toLowerCase().includes("nbd");
};

const isLowTierGcc = (formData: any): boolean => {
  const specs = (formData?.specs ?? "").toLowerCase().trim();
  const isGcc = specs.includes("gcc");
  // const dealDrivePrice = formData?.dealDriveEstimatedPrice ?? 0;
  const clientChosenPrice = Number(String(formData?.value ?? "0").replace(/,/g, ""));
  // const marketPrice = dealDrivePrice > 0 ? dealDrivePrice : clientChosenPrice > 0 ? clientChosenPrice : 0;
  const marketPrice = clientChosenPrice > 0 ? clientChosenPrice : 0;
  return isGcc && marketPrice > 0 && marketPrice <= 250_000;
};

const DUBAI_TIMEZONE = "Asia/Dubai";

type InspectionSubmitValues = Partial<BankValuationCompleteFormSchemaType> & {
  inspectionType?: "alba" | "home";
  inspectionDate?: string;
  inspectionTime?: string;
  inspectionLocation?: string;
  inspectionLatitude?: number;
  inspectionLongitude?: number;
};

const convert12HourTo24Hour = (time12h: string): { hours: number; minutes: number } => {
  const [time, period] = time12h.trim().split(" ");
  const [hourString, minuteString] = time.split(":");
  let hours = Number(hourString);
  const minutes = Number(minuteString);
  if (period.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (period.toUpperCase() === "AM" && hours === 12) hours = 0;
  return { hours, minutes };
};

const buildScheduledAtUtc = (inspectionDate: string, inspectionTime: string): string => {
  const [year, month, day] = inspectionDate.split("-").map(Number);
  const { hours, minutes } = convert12HourTo24Hour(inspectionTime);
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours - 4, minutes, 0, 0));
  return utcDate.toISOString();
};

const buildTimeSlotCode = (inspectionTime: string): string => {
  const { hours, minutes } = convert12HourTo24Hour(inspectionTime);
  const endDate = new Date(Date.UTC(2000, 0, 1, hours, minutes, 0, 0));
  endDate.setUTCHours(endDate.getUTCHours() + 2);
  const endHours = String(endDate.getUTCHours()).padStart(2, "0");
  const endMinutes = String(endDate.getUTCMinutes()).padStart(2, "0");
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}-${endHours}:${endMinutes}`;
};

// If the address is already a Google Maps URL (link tab), use it as-is.
// If we have real coords from map/drag/geolocation, build a pin URL from them.
const isGoogleMapsUrl = (val: string) =>
  val.includes("maps.google") ||
  val.includes("goo.gl/maps") ||
  val.includes("maps.app.goo.gl") ||
  val.includes("google.com/maps");

const buildGoogleMapsPinUrl = (address: string, lat?: number, lng?: number): string => {
  if (isGoogleMapsUrl(address)) return address;
  if (typeof lat === "number" && typeof lng === "number" && (lat !== DEFAULT_LAT || lng !== DEFAULT_LNG)) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  return address;
};

// Fixed indices — never change
const StepsNumber = {
  PersonalDetails: 0,
  CarDetails: 1,
  EstimatedPrice: 2,
  BankDetails: 3,
  Images: 4,
  Inspection: 5,
  Payment: 6,
};

export default function ClientFormWrapper({
  title,
  description,
  initialValues,
  initialStep = 0,
  resumeRequestId,
  stepLabels,
  stepTitles,
  buttonLabels,
}: ClientFormWrapperProps) {
  const tValidation = useTranslations("BankValuation.form.validation");
  const [apiResponse] = useState<SellCarRequestResponseDTO | null>(null);

  const [selectedBankName, setSelectedBankName] = useState<string>(initialValues?.bankName ?? "");
  const [selectedIsLowTierGcc, setSelectedIsLowTierGcc] = useState<boolean>(() =>
    initialValues ? isLowTierGcc(initialValues) : false,
  );

  useEffect(() => {
    if (resumeRequestId) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId, resumeRequestId);
    }
  }, [resumeRequestId]);

  // Full inspection step: ENBD + NOT low-tier GCC
  const showInspectionInStepper = isEnbdBank(selectedBankName) && !selectedIsLowTierGcc;

  // Simple location step: ENBD + IS low-tier GCC (no booking, just capture location)
  const showSimpleLocationInStepper = isEnbdBank(selectedBankName) && selectedIsLowTierGcc;

  const schemas = useMemo(
    () => ({
      [StepsNumber.PersonalDetails]: createBankValuationFirstStepSchema(tValidation),
      [StepsNumber.CarDetails]: createBankValuationSecondStepSchema(tValidation),
      [StepsNumber.EstimatedPrice]: createBankValuationEstimatedPriceStepSchema(tValidation),
      [StepsNumber.BankDetails]: createBankDetailsSchema(tValidation),
      [StepsNumber.Images]: BankValuationImagesStepSchema,
      [StepsNumber.Inspection]: BankValuationStripePaymentStepSchema,
      [StepsNumber.Payment]: BankValuationStripePaymentStepSchema,
    }),
    [tValidation],
  );

  const bankValuationFormSchema = useMemo(() => createBankValuationFormSchema(tValidation), [tValidation]);
  const bankValuationCompleteFormSchema = useMemo(
    () => createBankValuationCompleteFormSchema(tValidation),
    [tValidation],
  );

  const StepsLabels = {
    [StepsNumber.PersonalDetails]: stepLabels.personalDetails,
    [StepsNumber.CarDetails]: stepLabels.carDetails,
    [StepsNumber.EstimatedPrice]: stepLabels.estimatedPrice,
    [StepsNumber.BankDetails]: stepLabels.bankDetails,
    [StepsNumber.Images]: stepLabels.imagesAndDocuments,
    [StepsNumber.Inspection]: stepLabels.inspection,
    [StepsNumber.Payment]: stepLabels.payment,
  };

  const translatedStepTitles =
    showInspectionInStepper || showSimpleLocationInStepper
      ? [stepTitles.step1, stepTitles.step2, stepTitles.step3, stepTitles.step4, stepTitles.step5, stepTitles.step6]
      : [stepTitles.step1, stepTitles.step2, stepTitles.step3, stepTitles.step4, stepTitles.step5];

  const stepsData: StepData[] = [
    // ── 0 · Personal details ─────────────────────────────────────────────────
    {
      component: BankValuationPersonalInfoStep,
      onStepSubmit: async (_formData: FormData, _previousFormData: FormData) => {},
      step: { label: StepsLabels[StepsNumber.PersonalDetails], index: StepsNumber.PersonalDetails },
    },

    // ── 1 · Car details ──────────────────────────────────────────────────────
    {
      component: BankValuationCarDetailsStep,
      onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
        if (!previousFormData || Object.keys(previousFormData).length === 0) return;

        const parsedData = schemas[StepsNumber.PersonalDetails].parse(
          previousFormData,
        ) as BankValuationFirstStepSchemaType;

        const response = await getOrCreateLead({
          ...parsedData,
          source: "bank-valuation",
          status: "ABANDONED_FORM",
          webhookData: {
            bankValuationDetails: { ...previousFormData, ...formData },
            vehicleImageUrls: [],
            source: "bank-valuation-form",
            ...(parsedData.marketing ? { marketingDetails: parsedData.marketing } : {}),
          },
        });

        if (response.error) {
          toast.error("Failed to save or retrieve lead information");
          return;
        }

        if (!response.isNew) {
          await notifyWebhookAboutBankValuationUpdate({
            lead: { ...parsedData, id: response.data!.id },
            bankValuationDetails: { ...previousFormData, ...formData },
            vehicleImageUrls: [],
            message: "Existing lead requesting bank valuation",
            source: "bank-valuation-form",
            ...(parsedData.marketing ? { marketingDetails: parsedData.marketing } : {}),
          });
        }
      },
      step: { label: StepsLabels[StepsNumber.CarDetails], index: StepsNumber.CarDetails },
    },

    // ── 2 · Estimated price ──────────────────────────────────────────────────
    {
      component: BankValuationEstimatedPriceStep,
      skipZodValidation: true,
      onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
        const data = { ...formData, ...previousFormData } as any;
        const imageUrls = (data["images"] ?? []) as unknown as any[];

        const parsedData = bankValuationCompleteFormSchema.parse({
          ...data,
          images: imageUrls.map((image: { url: string }) => image.url),
          bankerEmail: data["bankerEmail"] || "placeholder@email.com",
          bankName: data["bankName"] || "Unknown",
          bankApplicationId: data["bankApplicationId"] || "N/A",
        });

        const response = await createBankValuationRequest(parsedData);
        localStorage.setItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId, response.data?.id ?? "");
      },
      step: { label: StepsLabels[StepsNumber.EstimatedPrice], index: StepsNumber.EstimatedPrice },
    },

    // ── 3 · Bank details ─────────────────────────────────────────────────────
    {
      component: BankValuationBankerDetailsStep,
      onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
        const submittedBankName = (formData as any)?.bankName ?? (previousFormData as any)?.bankName ?? "";
        setSelectedBankName(submittedBankName);
        setSelectedIsLowTierGcc(isLowTierGcc({ ...(previousFormData as any), ...(formData as any) }));

        const data = previousFormData as any;
        const newData = formData as any;
        const imageUrls = (data["images"] ?? []) as unknown as any[];
        const { images: _dataImages, marketing, ...dataWithoutImages } = { ...(previousFormData as any) };

        const parsedData = bankValuationCompleteFormSchema.parse({
          ...data,
          ...newData,
          images: imageUrls.map((image: { url: string }) => image.url),
        });

        Promise.resolve()
          .then(async () => {
            const response = await createBankValuationRequest(parsedData);
            localStorage.setItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId, response.data?.id ?? "");
          })
          .catch((error) => console.error("Error creating or updating bank valuation request:", error));

        await notifyWebhookAboutBankValuationUpdate({
          lead: { name: dataWithoutImages.name, phone: dataWithoutImages.phone },
          bankValuationDetails: { ...dataWithoutImages, ...formData },
          vehicleImageUrls: imageUrls?.map((image) => image.url),
          message: "Client updated with bank details",
          source: "bank-valuation-form",
          ...(marketing ? { marketingDetails: marketing } : {}),
        });
      },
      step: { label: StepsLabels[StepsNumber.BankDetails], index: StepsNumber.BankDetails },
    },

    // ── 4 · Images ───────────────────────────────────────────────────────────
    {
      component: BankValuationImagesStep,
      skipZodValidation: true,
      onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
        const data = formData as any;
        const imageUrls = (data["images"] ?? []) as unknown as any[];
        const { images: _dataImages, ...dataWithoutImages } = { ...(formData as any) };
        const { images: _prevDataImages, marketing, ...prevDataWithoutImages } = { ...(previousFormData as any) };

        const parsedData = bankValuationCompleteFormSchema.parse({
          ...previousFormData,
          images: imageUrls.map((image: { url: string }) => image.url),
        });

        await new Promise<void>((resolve) => {
          Promise.resolve()
            .then(async () => {
              const response = await createBankValuationRequest(parsedData);
              localStorage.setItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId, response.data?.id ?? "");
              resolve();
            })
            .catch((error) => {
              console.error("Error creating or updating bank valuation request:", error);
              resolve();
            });
        });

        await notifyWebhookAboutBankValuationUpdate({
          lead: { name: prevDataWithoutImages.name, phone: prevDataWithoutImages.phone },
          bankValuationDetails: { ...prevDataWithoutImages, ...dataWithoutImages },
          vehicleImageUrls: imageUrls?.map((image) => image.url),
          message: "Client updated with car images",
          source: "bank-valuation-form",
          ...(marketing ? { marketingDetails: marketing } : {}),
        });

        // For low-tier GCC + ENBD: the "perfect inspection" payload is sent
        // in the SimpleLocation step's onStepSubmit (below), not here.
        // We intentionally do NOT call updateBankValuationInspectionPerfect here
        // anymore — that call is now deferred until the user provides their location.
      },
      step: { label: StepsLabels[StepsNumber.Images], index: StepsNumber.Images },
    },

    // ── 4b · Simple location (ENBD + low-tier GCC only) ──────────────────────
    // Inserted between Images and Payment. Collects the customer's location
    // and sends the "perfect inspection" payload with it.
    ...(showSimpleLocationInStepper
      ? [
          {
            component: BankValuationSimpleLocationStep,
            skipZodValidation: true,
            onStepSubmit: async (_formData: FormData, allFormData: FormData) => {
              const allData = allFormData as unknown as InspectionSubmitValues;
              const valuationId = localStorage.getItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId) ?? "";

              if (!valuationId) {
                toast.error("Unable to continue. Please retry the previous step.");
                throw new Error("Missing pending valuation request ID before simple location submit");
              }
              const address = allData.inspectionLocation ?? "";
              const lat = allData.inspectionLatitude;
              const lng = allData.inspectionLongitude;

              if (!address.trim()) {
                toast.error("Please provide your location before continuing.");
                throw new Error("Location is required");
              }

              const addressText = address;
              const googleMapsPinUrl = buildGoogleMapsPinUrl(address, lat, lng);

              const now = new Date();
              const inspectionDate = now.toISOString().split("T")[0];

              await updateBankValuationInspectionPerfect(valuationId, {
                requiresInspection: true,
                status: "booked" as const,
                inspectionType: "alba",
                inspectionDate,
                scheduledAtUtc: now.toISOString(),
                addressText,
                googleMapsPinUrl,
                timezone: DUBAI_TIMEZONE,
                timeSlotCode: "00:00-00:00",
              }).catch((err) => console.error("Failed to send perfect inspection:", err));
            },
            step: { label: stepLabels.yourLocation, index: StepsNumber.Inspection },
          },
        ]
      : []),

    // ── 5 · Full inspection (ENBD + high-tier / non-GCC only) ────────────────
    ...(showInspectionInStepper
      ? [
          {
            component: BankValuationInspectionStep,
            skipZodValidation: true,
            onStepSubmit: async (_formData: FormData, allFormData: FormData) => {
              const inspectionData = allFormData as unknown as InspectionSubmitValues;
              const valuationId = localStorage.getItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId) ?? "";
              const allData = allFormData as any;

              if (!valuationId) {
                toast.error("Unable to continue. Please retry the previous step.");
                throw new Error("Missing pending valuation request ID before inspection submit");
              }

              if (
                !inspectionData.inspectionType ||
                !inspectionData.inspectionDate ||
                !inspectionData.inspectionTime ||
                !inspectionData.inspectionLocation
              ) {
                toast.error("Please complete inspection type, date, time and location.");
                throw new Error("Incomplete inspection details");
              }

              const scheduledAtUtc = buildScheduledAtUtc(inspectionData.inspectionDate, inspectionData.inspectionTime);
              const timeSlotCode = buildTimeSlotCode(inspectionData.inspectionTime);
              const googleMapsPinUrl = buildGoogleMapsPinUrl(
                inspectionData.inspectionLocation,
                inspectionData.inspectionLatitude,
                inspectionData.inspectionLongitude,
              );

              const inspectionPayload = {
                requiresInspection: true,
                status: "booked" as const,
                inspectionType: inspectionData.inspectionType,
                inspectionDate: inspectionData.inspectionDate,
                scheduledAtUtc,
                addressText: inspectionData.inspectionLocation,
                googleMapsPinUrl,
                timezone: DUBAI_TIMEZONE,
                timeSlotCode,
              };

              await updateBankValuationInspection(valuationId, inspectionPayload);

              const imageUrls = ((allData.images ?? []) as Array<{ url?: string }>)
                .filter((image) => !!image?.url)
                .map((image) => image.url as string);

              const { images: _images, marketing, ...bankValuationDetailsWithoutImages } = allData;

              await notifyWebhookAboutBankValuationUpdate({
                lead: {
                  name: bankValuationDetailsWithoutImages.name,
                  phone: bankValuationDetailsWithoutImages.phone,
                },
                bankValuationDetails: {
                  ...bankValuationDetailsWithoutImages,
                  requiresInspection: true,
                  inspectionStatus: "booked",
                  inspectionType: inspectionData.inspectionType,
                  inspectionDate: inspectionData.inspectionDate,
                  inspectionTime: inspectionData.inspectionTime,
                  scheduledAtUtc,
                  inspectionLocation: inspectionData.inspectionLocation,
                  googleMapsPinUrl,
                  inspectionTimezone: DUBAI_TIMEZONE,
                  timeSlotCode,
                  valuationRequestId: valuationId,
                },
                vehicleImageUrls: imageUrls,
                message: "Client booked inspection",
                source: "bank-valuation-form",
                ...(marketing ? { marketingDetails: marketing } : {}),
              });
            },
            step: { label: StepsLabels[StepsNumber.Inspection], index: StepsNumber.Inspection },
          },
        ]
      : []),

    // ── 6 · Payment ──────────────────────────────────────────────────────────
    {
      component: BankValuationStripePaymentStep,
      onStepSubmit: async (_formData: FormData, _previousFormData: FormData) => {},
      step: { label: StepsLabels[StepsNumber.Payment], index: StepsNumber.Payment },
      skipSubmit: true,
    },

    // ── Success ───────────────────────────────────────────────────────────────
    {
      component: SuccessStep,
      onStepSubmit: async (_formData: FormData) => {},
      isSuccessStep: true,
    },
  ];

  const handleFinalSubmit = async (data: BankValuationFormSchemaType) => {
    try {
      const parsedData = bankValuationFormSchema.parse(data);
      console.log("Parsed data", parsedData);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit your sell request.");
    }
  };

  const completedSteps = Array.from({ length: initialStep }, (_, i) => i);

  return (
    <MultistepForm<BankValuationCompleteFormSchemaType>
      stepsData={stepsData.map((step) => ({
        ...step,
        component:
          step.component === SuccessStep
            ? (props: any) => <SuccessStep {...props} apiResponse={apiResponse} />
            : step.component,
      }))}
      onSubmit={handleFinalSubmit}
      stepTitles={translatedStepTitles}
      title={title}
      description={description}
      schemas={schemas}
      stepIndicatorClassName="!w-[45rem] !max-w-[600px] sm:!max-w-[700px] md:!max-w-full md:!w-full "
      className="!max-w-full"
      containerId={CONTAINER_IDS.bankValuation}
      initialValues={initialValues}
      initialStep={initialStep}
      initialCompletedSteps={completedSteps}
      buttonLabels={buttonLabels}
    />
  );
}
